import {
  tRelative
} from "./chunk-XNJ3V4UZ.mjs";
import {
  DAYS_OF_WEEK
} from "./chunk-YK5NUG2I.mjs";
import {
  getActiveDateLocaleTag
} from "./chunk-2J3DUGJJ.mjs";

// src/dateFns.ts
import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  isWithinInterval,
  startOfDay,
  addDays,
  getDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  format
} from "date-fns";
var parseDateString = (dateStr) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return { year, month, day };
};
var dateStringToDate = (dateStr) => {
  const { year, month, day } = parseDateString(dateStr);
  return new Date(year, month - 1, day);
};
var today = () => startOfDay(/* @__PURE__ */ new Date());
var todayString = () => toDateString(/* @__PURE__ */ new Date());
var daysBetweenDates = (a, b) => Math.abs(differenceInCalendarDays(a, b));
var monthsSince = (since, now = /* @__PURE__ */ new Date()) => {
  const start = typeof since === "string" ? new Date(since) : since;
  if (Number.isNaN(start.getTime())) return 0;
  return Math.max(0, differenceInCalendarMonths(now, start));
};
var toDateString = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
var nightsBetween = (checkIn, checkOut) => {
  const a = dateStringToDate(checkIn);
  const b = dateStringToDate(checkOut);
  return Math.round((b.getTime() - a.getTime()) / 864e5);
};
var formatDateShort = (dateStr) => dateStringToDate(dateStr).toLocaleDateString(getActiveDateLocaleTag(), {
  month: "short",
  day: "numeric"
});
var formatStayRange = (checkIn, checkOut, { weekday = true } = {}) => {
  const locale = getActiveDateLocaleTag();
  const start = dateStringToDate(checkIn);
  const end = dateStringToDate(checkOut);
  const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
  const base = {
    ...weekday ? { weekday: "short" } : {},
    month: "short",
    day: "numeric"
  };
  const endOpts = end.getFullYear() === currentYear ? base : { ...base, year: "numeric" };
  return `${start.toLocaleDateString(locale, base)} \u2013 ${end.toLocaleDateString(locale, endOpts)}`;
};
var formatCompactDateRange = (start, end, { weekday = false } = {}) => {
  const locale = getActiveDateLocaleTag();
  const from = dateStringToDate(start);
  const to = end ? dateStringToDate(end) : null;
  const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
  const monthDay = {
    ...weekday ? { weekday: "short" } : {},
    month: "short",
    day: "numeric"
  };
  const withYear = (d) => d.getFullYear() === currentYear ? monthDay : { ...monthDay, year: "numeric" };
  if (!to || to.getTime() === from.getTime()) {
    return from.toLocaleDateString(locale, withYear(from));
  }
  const sameMonth = from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth();
  if (!weekday && sameMonth && to.getFullYear() === currentYear) {
    return `${from.toLocaleDateString(locale, monthDay)}\u2013${to.toLocaleDateString(locale, { day: "numeric" })}`;
  }
  return `${from.toLocaleDateString(locale, monthDay)} \u2013 ${to.toLocaleDateString(locale, withYear(to))}`;
};
var formatDateRange = (range) => {
  if (range.start && range.end)
    return `${formatDateShort(range.start)} \u2013 ${formatDateShort(range.end)}`;
  if (range.start) return `${formatDateShort(range.start)} \u2013 ?`;
  return null;
};
var formatDatePicker = (dateStr) => {
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};
var parseCalendarView = (dateStr) => {
  const { year, month } = parseDateString(dateStr);
  return { year, month: month - 1 };
};
var toDayOfWeekFromDateString = (dateStr) => DAYS_OF_WEEK[dateStringToDate(dateStr).getDay()];
var formatTimeRemaining = (expiresAt) => {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return tRelative("relative.expired");
  const hours = Math.floor(diff / (1e3 * 60 * 60));
  const minutes = Math.floor(diff % (1e3 * 60 * 60) / (1e3 * 60));
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};
var formatDuration = (ms) => {
  if (ms == null) return "\u2014";
  if (ms < 1e3) return `${ms}ms`;
  const s = ms / 1e3;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
};
var formatTravelDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "\u2014";
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return tRelative("relative.minutesCompact", { count: minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hoursLabel = tRelative("relative.hoursCompact", { count: hours });
  if (rest === 0) return hoursLabel;
  return `${hoursLabel} ${tRelative("relative.minutesCompact", { count: rest })}`;
};
var formatSessionDuration = (minutes) => minutes == null || !Number.isFinite(minutes) || minutes <= 0 ? "" : formatTravelDuration(minutes * 60);
var formatTimeAgo = (iso) => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1e3));
  if (secs < 60) return tRelative("relative.now");
  const mins = Math.floor(secs / 60);
  if (mins < 60) return tRelative("relative.minutesCompact", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return tRelative("relative.hoursCompact", { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return tRelative("relative.daysCompact", { count: days });
  return tRelative("relative.weeksCompact", { count: Math.floor(days / 7) });
};

export {
  differenceInCalendarDays,
  isWithinInterval,
  startOfDay,
  addDays,
  getDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  format,
  parseDateString,
  dateStringToDate,
  today,
  todayString,
  daysBetweenDates,
  monthsSince,
  toDateString,
  nightsBetween,
  formatDateShort,
  formatStayRange,
  formatCompactDateRange,
  formatDateRange,
  formatDatePicker,
  parseCalendarView,
  toDayOfWeekFromDateString,
  formatTimeRemaining,
  formatDuration,
  formatTravelDuration,
  formatSessionDuration,
  formatTimeAgo
};
